import React, { CSSProperties, ReactNode } from 'react';
import { INode } from '../../helpers/interfaces';
import { SENSORS } from '../../helpers/Devices';
import { SYSTEM_VERSIONS } from '../../helpers/Systems';

interface NodeExtendedInfoProps {
	node: INode;
	changed?: Date;
	accountCategory: string;
}

const wrapperStyle: CSSProperties = {
	borderTop: '1px solid #eee',
	color: '#6c6c6c',
	display: 'flex',
	paddingTop: '10px',
	fontWeight: 400,
	flexWrap: 'wrap',
};

const parameterStyle = { marginRight: '20px' };
const parameterNameStyle = { marginBottom: '0' };
const parameterDataStyle = { fontWeight: 700 };

const roleMap = {
  access_device: "AD",
  endpoint_device: "MD",
  retransmitter: "RD",
  gateway: "Gateway",
  unknown: "unknown"
};

interface Param {
	label: string;
	value: ReactNode;
	title?: string;
}

const Parameter = ({ label, value, title }: Param) => (
	<div style={parameterStyle} title={title}>
		<p style={parameterNameStyle}>{label}</p>
		<p style={parameterDataStyle}>{value}</p>
	</div>
);

const parseSignalQualityInfo = (raw: string | null | undefined) => {
	if (!raw) return null;
	try {
		return JSON.parse(raw)?.[0] ?? null;
	} catch {
		return null;
	}
};

const NodeExtendedInfo = ({ node, accountCategory }: NodeExtendedInfoProps) => {
	if (accountCategory !== 'installer' && accountCategory !== 'wisene') {
		return null;
	}

	const vcc = node?.SupplyVoltage1 ? `${node.SupplyVoltage1.toFixed(1).toString()}V` : '--';
	const vdd = node?.SupplyVoltage2 ? `${node.SupplyVoltage2.toFixed(1).toString()}V` : '--';
	const measuredAngle = node.MeasuredAngle ? `${node.MeasuredAngle.toFixed(3)}°` : "--";
	const measuredDistance = node.MeasuredDistance ? `${node.MeasuredDistance}mm` : "--";
	const measurementBaseAngle = node?.MeasurementBaseAngle ? `${node.MeasurementBaseAngle.toFixed(3).toString()}°` : '--';
	const measurementBaseDistance = node?.MeasurementBaseDistance ? `${node.MeasurementBaseDistance.toString()}mm` : '--';
	const measurementTimestamp = new Date(node.LatestMeasurementTimestamp).toLocaleString();
	const serial = node.DeviceSerialString ? node.DeviceSerialString.toUpperCase() : "--";
	const subSerial = node.DeviceSubSerialString ? node.DeviceSubSerialString.toUpperCase() : "--";
	const systemVersionLabel = node.SystemVersion === SYSTEM_VERSIONS.W2 ? 'WSN' : 'LoRa';
	const permissibleValue =
		node.PermissibleValue != null
			? (node.MainSensorType === SENSORS.Iclinometer ? Number(node.PermissibleValue).toFixed(3) + "°" : node.PermissibleValue + "mm")
			: "--";
	const dependenciesFormula = node.DependenciesFormula;
	const deviceKindTypeSubtype = node.DeviceRole == 'gateway' ? 'CU' : (roleMap[node.DeviceRole] || "unknown") + `/${node.DeviceType}`;
	const firmwareVersion = node.FirmwareVersion;
	const firmwareVersionLabel = node.DeviceRole == 'gateway' ? 'Software Version:' : 'Firmware Revision:';


	// -- Shared params --
	const versionParam: Param = { label: 'Version: ', value: systemVersionLabel };
	const firmwareParam: Param = { label: firmwareVersionLabel, value: firmwareVersion };
	const deviceKindParam: Param = { label: 'Device Kind/Type/Subtype: ', value: deviceKindTypeSubtype };
	const dependenciesFormulaParam: Param | null = dependenciesFormula
		? { label: 'Dependencies formula: ', value: dependenciesFormula }
		: null;
	const vccParam: Param = { label: 'VCC: ', value: vcc };
	const vddParam: Param = { label: 'VDD: ', value: vdd };
	const permissibleValueParam: Param = { label: 'Permissible value: ', value: permissibleValue };
	const measuredAngleParam: Param = { label: 'Measured angle: ', value: measuredAngle, title: measurementTimestamp };
	const measuredDistanceParam: Param = { label: 'Measured distance: ', value: measuredDistance, title: measurementTimestamp };
	const baseAngleParam: Param = { label: 'Base angle: ', value: measurementBaseAngle };
	const baseDistanceParam: Param = { label: 'Base distance: ', value: measurementBaseDistance };
	const deviceSerialParam: Param = { label: node.SystemVersion === SYSTEM_VERSIONS.W2 ? 'Device SN: ' : 'Device EUI:', value: serial };



	// --- W2 / WSN params---

	if (node.SystemVersion === SYSTEM_VERSIONS.W2) {
		if (node.MainSensorType === SENSORS.None) {
			const params: Param[] = [
				deviceSerialParam,
				deviceKindParam,
				versionParam,
				firmwareParam,
			];
			return (
				<div style={wrapperStyle}>
					{params.map((p, i) => <Parameter key={i} {...p} />)}
				</div>
			);
		}

		const sensorParams: Param[] = node.MainSensorType === SENSORS.Iclinometer
			? [
				measuredAngleParam,
				baseAngleParam,
				{ label: 'Sensor SN: ', value: subSerial },
			]
			: [
				measuredDistanceParam,
				baseDistanceParam,
			];

		const params: Param[] = [
			vccParam,
			vddParam,
			...sensorParams,
			permissibleValueParam,
			deviceSerialParam,
			deviceKindParam,
			...(dependenciesFormulaParam ? [dependenciesFormulaParam] : []),
			versionParam,
			firmwareParam,
		];
		return (
			<div style={wrapperStyle}>
				{params.map((p, i) => <Parameter key={i} {...p} />)}
			</div>
		);
	}



	// --- W4 LoRaWAN / LoRa params---

	if (!node.SignalQualityInfo) {
		const params: Param[] = [
			...(node.DeviceRole === 'gateway' ? [
				deviceSerialParam,
				{ label: 'Device Kind/Type/Subtype: ', value: node.DeviceType },
			] : []),
			deviceKindParam,
			versionParam,
			...(node.DeviceRole === 'gateway' ? [firmwareParam] : []),
		];
		return (
			<div style={wrapperStyle}>
				{params.map((p, i) => <Parameter key={i} {...p} />)}
			</div>
		);
	}

	const signalInfo = parseSignalQualityInfo(node.SignalQualityInfo);
	const signalRSSI = signalInfo?.['rssi'] != null ? `${signalInfo['rssi']}dBm` : '--';
	const signalLoRaSNR = signalInfo?.['snr'] != null ? `${signalInfo['snr'].toFixed(1)}dB` : '--';
	const signalQualityInfoTimestamp = new Date(node.SignalQualityInfoTimestamp).toLocaleString();
	const gatewayId = signalInfo?.['gatewayId'] ? `${signalInfo['gatewayId'].toUpperCase()}` : null;
	const inclinometerSerial = node?.inclinometerSerial ?? '--';

	const params: Param[] = [
		{ label: 'Signal:', value: signalRSSI, title: signalQualityInfoTimestamp },
		{ label: 'LoRaSNR: ', value: signalLoRaSNR, title: signalQualityInfoTimestamp },
		vccParam,
		vddParam,
		measuredAngleParam,
		measuredDistanceParam,
		baseAngleParam,
		baseDistanceParam,
		permissibleValueParam,
		deviceSerialParam,
		{ label: 'Gateway EUI:', value: gatewayId ?? '--', title: signalQualityInfoTimestamp },
		...(node.MainSensorType === SENSORS.Iclinometer ? [{ label: ' Sensor SN:', value: inclinometerSerial }] : []),
		deviceKindParam,
		...(dependenciesFormulaParam ? [dependenciesFormulaParam] : []),
		versionParam,
		firmwareParam,
	];

	return (
		<div style={wrapperStyle}>
			{params.map((p, i) => <Parameter key={i} {...p} />)}
		</div>
	);
};

export { NodeExtendedInfo };
